import { useQuery } from '@tanstack/react-query';
import { Check, Copy } from 'lucide-react';
import { useState } from 'react';
import { api } from '../lib/api';
import { cn } from '../lib/cn';
import { ruleLabel } from '../lib/rule-labels';
import { formatDeviceType, parseUa } from '../lib/ua';
import { EventTimeline } from './EventTimeline';
import { SignalsView } from './SignalsView';
import { useToast } from './Toaster';
import { Badge } from './ui/badge';
import { Card } from './ui/card';

interface ProbeData {
  id: string;
  sessionId: string;
  createdAt: number;
  ip: string | null;
  country: string | null;
  city: string | null;
  region: string | null;
  asn: number | null;
  asnOrg: string | null;
  isDatacenter: number;
  cfVerifiedBot: number;
  userAgent: string | null;
  acceptLanguage: string | null;
  tlsVersion: string | null;
  httpVersion: string | null;
  ja4: string | null;
  colo: string | null;
  clientHintUa: string | null;
  clientHintMobile: string | null;
  clientHintPlatform: string | null;
}

interface AuditDetail {
  id: string;
  createdAt: number;
  decision: 'pass' | 'block' | 'pending';
  score: number;
  startScore?: number;
  version?: string;
  scoreBreakdown?: string;
  reasons: string;
  ip: string | null;
  country: string | null;
  asn: number | null;
  asnOrg: string | null;
  isDatacenter: number;
  cfVerifiedBot: number;
  userAgent: string | null;
  referer: string | null;
  acceptLanguage: string | null;
  tlsVersion: string | null;
  ja4: string | null;
  colo: string | null;
  ttclid: string | null;
  fbclid: string | null;
  gclid: string | null;
  host: string | null;
  pageHref: string | null;
  clientSignals: string;
  dwellMs: number;
  mouseEvents: number;
  touchEvents: number;
  deviceId: string | null;
  deviceFp: string | null;
  deviceLinkCount: number;
  deviceSeenCount: number;
  probe: ProbeData | null;
}

/** Audit detail body — shared by the full page route and the audits-list drawer.
 *  Renders the decision header + all signal cards. Caller provides the container. */
export function AuditDetailView({ id }: { id: string }) {
  const { data } = useQuery({
    queryKey: ['audit', id],
    queryFn: () => api<AuditDetail>(`/api/audits/${id}`),
  });

  if (!data) return <div className="text-muted-foreground p-6 text-sm">加载中…</div>;

  const reasons = JSON.parse(data.reasons) as string[];
  const signals = JSON.parse(data.clientSignals) as Record<string, unknown>;
  const breakdown = data.scoreBreakdown
    ? (JSON.parse(data.scoreBreakdown) as { rule: string; weight: number }[])
    : [];
  const startScore = data.startScore ?? 100;
  const totalDeducted = breakdown.reduce((a, b) => a + b.weight, 0);
  const ua = parseUa(data.userAgent);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <h1 className="font-mono text-lg font-semibold">{data.id}</h1>
        <Badge
          variant={
            data.decision === 'pass'
              ? 'success'
              : data.decision === 'pending'
                ? 'info'
                : 'destructive'
          }
        >
          {data.decision === 'pending' ? '未验证' : data.decision}
        </Badge>
        <span className="text-muted-foreground text-sm">得分 {data.score}</span>
        {data.version && (
          <Badge variant="secondary" className="font-mono" title="产生该记录的代码版本">
            {data.version}
          </Badge>
        )}
      </div>

      {data.decision === 'pending' && (
        <div className="text-muted-foreground mb-4 rounded-md border border-zinc-500/30 bg-zinc-500/5 p-3 text-xs">
          <span className="text-foreground font-medium">未完成验证</span> —— 访客着陆后在 verify
          触发前离开（爬虫秒退 / 切后台 / 8s 内未点击），因此
          <b>无判定、无指纹深度、无跨链统计</b>
          （均需 verify）。probe 服务端信号、着陆快照与离开前的部分行为轨迹可用。
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Card className="gap-3 p-5">
          <h2 className="text-sm font-semibold">得分明细</h2>
          {breakdown.length === 0 ? (
            <div>
              <div className="flex items-center justify-between border-b py-1.5 text-sm">
                <span className="text-muted-foreground">起始得分</span>
                <span className="font-mono tabular-nums">{startScore}</span>
              </div>
              <div className="mt-2 flex items-center justify-between text-sm">
                <span className="font-medium">最终得分</span>
                <span className="font-mono text-lg tabular-nums text-emerald-600 dark:text-emerald-400">
                  {data.score}
                </span>
              </div>
              {reasons.length === 0 ? (
                <p className="text-muted-foreground mt-3 text-xs">未命中任何规则</p>
              ) : (
                <ul className="text-muted-foreground mt-3 space-y-1.5 text-xs">
                  {reasons.map((r) => (
                    <li key={r} className="flex items-center gap-2">
                      <span className="bg-destructive size-1.5 shrink-0 rounded-full" />
                      <span>{ruleLabel(r)}</span>
                      <span className="text-muted-foreground/70 font-mono text-[10px]">
                        (旧记录无明细)
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between border-b py-1.5 text-sm">
                <span className="text-muted-foreground">起始得分</span>
                <span className="font-mono tabular-nums">{startScore}</span>
              </div>
              <ul className="space-y-1 py-2 text-sm">
                {breakdown.map((b, i) => (
                  <li
                    key={`${b.rule}-${i}`}
                    className="flex items-center justify-between border-b py-1.5 last:border-0"
                  >
                    <span className="text-foreground/80 flex items-center gap-2">
                      <span className="bg-destructive size-1.5 shrink-0 rounded-full" />
                      {ruleLabel(b.rule)}
                    </span>
                    <span className="text-destructive font-mono tabular-nums">-{b.weight}</span>
                  </li>
                ))}
              </ul>
              <div className="flex items-center justify-between border-t pt-2 text-sm">
                <span className="text-muted-foreground">扣分合计</span>
                <span className="text-destructive font-mono tabular-nums">-{totalDeducted}</span>
              </div>
              <div className="mt-1 flex items-center justify-between text-sm">
                <span className="font-medium">最终得分</span>
                <span
                  className={cn(
                    'font-mono text-lg tabular-nums',
                    data.decision === 'pass'
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-destructive',
                  )}
                >
                  {data.score}
                </span>
              </div>
            </div>
          )}
        </Card>
        <Card className="gap-1 p-5">
          <h2 className="mb-2 text-sm font-semibold">边缘信号</h2>
          <Field k="IP" v={data.ip} mono />
          <Field k="国家" v={data.country} />
          <Field k="ASN" v={data.asn ? `AS${data.asn} ${data.asnOrg ?? ''}` : null} />
          <Field k="IP 类型" v={data.isDatacenter ? '数据中心 / 托管' : '住宅 / 移动'} />
          <Field k="Verified Bot" v={data.cfVerifiedBot ? '是' : '否'} />
          <Field k="TLS" v={data.tlsVersion} mono />
          <Field k="JA4" v={data.ja4} mono />
          <Field k="Colo" v={data.colo} mono />
          <Field k="ttclid" v={data.ttclid} mono />
          <Field k="fbclid" v={data.fbclid} mono />
          <Field k="gclid" v={data.gclid} mono />
          <Field k="域名" v={data.host} />
          <Field k="完整 URL" v={data.pageHref} />
          <Field k="Referer" v={data.referer} />
        </Card>
        <Card className="gap-1 p-5">
          <div className="mb-2 flex items-center gap-2">
            <h2 className="text-sm font-semibold">设备识别</h2>
            {data.deviceLinkCount >= 2 && (
              <Badge variant="destructive" title="同一设备访问了多个不同链接，疑似踩点">
                跨 {data.deviceLinkCount} 链接
              </Badge>
            )}
            {data.deviceSeenCount >= 2 && data.deviceLinkCount < 2 && (
              <Badge variant="secondary">复访 {data.deviceSeenCount} 次</Badge>
            )}
          </div>
          {data.deviceId ? (
            <>
              <Field k="设备 UUID" v={data.deviceId} mono />
              <Field k="跨链接数 (去重)" v={data.deviceLinkCount || '-'} />
              <Field k="该设备总访问" v={data.deviceSeenCount || '-'} />
              <Field k="指纹哈希" v={data.deviceFp} mono />
              <p className="text-muted-foreground mt-1 text-[11px]">
                {data.decision === 'pending'
                  ? '未完成验证：跨链接数 / 总访问 / 指纹哈希在 verify 时才计算，本次未触发，故为空。'
                  : '跨链接数基于持久 UUID；指纹哈希仅作聚类参考（移动端机型会撞车，不参与评分）。'}
              </p>
            </>
          ) : (
            <p className="text-muted-foreground text-xs">无设备 ID（旧记录或未执行客户端脚本）</p>
          )}
        </Card>
        {data.probe && (
          <Card className="gap-1 p-5 sm:col-span-2">
            <h2 className="text-sm font-semibold">Probe 服务端信号</h2>
            <p className="text-muted-foreground mb-2 text-[11px]">
              页面加载时通过 /api/probe 采集的服务端视角信号（早于 verify 约 1 秒）
            </p>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 sm:grid-cols-3">
              <Field k="Probe IP" v={data.probe.ip} mono />
              <Field
                k="IP 一致性"
                v={
                  data.probe.ip && data.ip
                    ? data.probe.ip === data.ip
                      ? '✅ 一致'
                      : `❌ 不一致 (probe: ${data.probe.ip})`
                    : '-'
                }
              />
              <Field k="国家" v={data.probe.country} />
              <Field k="城市" v={data.probe.city} />
              <Field k="地区" v={data.probe.region} />
              <Field
                k="ASN"
                v={data.probe.asn ? `AS${data.probe.asn} ${data.probe.asnOrg ?? ''}` : null}
              />
              <Field k="数据中心" v={data.probe.isDatacenter ? '是' : '否'} />
              <Field k="Verified Bot" v={data.probe.cfVerifiedBot ? '是' : '否'} />
              <Field k="TLS" v={data.probe.tlsVersion} mono />
              <Field k="HTTP 版本" v={data.probe.httpVersion} mono />
              <Field k="JA4" v={data.probe.ja4} mono />
              <Field k="Colo" v={data.probe.colo} mono />
              <Field k="sec-ch-ua" v={data.probe.clientHintUa} mono />
              <Field k="sec-ch-ua-mobile" v={data.probe.clientHintMobile} mono />
              <Field k="sec-ch-ua-platform" v={data.probe.clientHintPlatform} mono />
              <Field k="Accept-Language" v={data.probe.acceptLanguage} />
            </div>
          </Card>
        )}
        <Card className="gap-3 p-5 sm:col-span-2">
          <h2 className="text-sm font-semibold">UA 解析</h2>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 sm:grid-cols-4">
            <Field k="设备类型" v={formatDeviceType(ua.deviceType)} />
            <Field k="厂商" v={ua.deviceVendor} />
            <Field k="型号" v={ua.deviceModel} />
            <Field
              k="浏览器"
              v={ua.browser ? `${ua.browser} ${ua.browserVersion ?? ''}`.trim() : null}
            />
            <Field k="系统" v={ua.os ? `${ua.os} ${ua.osVersion ?? ''}`.trim() : null} />
            <Field k="引擎" v={ua.engine} />
          </div>
          <details>
            <summary className="text-muted-foreground hover:text-foreground cursor-pointer text-xs">
              原始 User-Agent
            </summary>
            <pre className="bg-muted mt-2 max-h-32 overflow-auto rounded-md p-2 text-[11px]">
              {data.userAgent}
            </pre>
          </details>
        </Card>
        <Card className="gap-3 p-5 sm:col-span-2">
          <h2 className="text-sm font-semibold">行为信号</h2>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <Stat label="停留时长" value={`${data.dwellMs}ms`} />
            <Stat label="鼠标事件" value={data.mouseEvents} />
            <Stat label="触摸事件" value={data.touchEvents} />
          </div>
          {data.decision === 'pending' && (
            <p className="text-muted-foreground text-[11px]">
              未完成验证：完整行为信号随 verify 采集，本次未触发；此处仅为页面卸载快照（可能为 0）。
            </p>
          )}
        </Card>
        <Card className="gap-3 p-5 sm:col-span-2">
          <h2 className="text-sm font-semibold">埋点交互时间线</h2>
          {data.decision === 'pending' && (
            <p className="text-muted-foreground text-[11px]">
              未完成验证：verify 未跑完（常见于页面被切到后台 / 在 webview 中挂起，8s
              计时器被节流暂停）。下方轨迹是页面卸载时补发的部分缓冲（到离开为止）；早期记录或极快秒退可能为空。
            </p>
          )}
          <EventTimeline auditId={data.id} />
        </Card>
        <Card className="gap-3 p-5 sm:col-span-2">
          <h2 className="text-sm font-semibold">客户端信号</h2>
          <SignalsView signals={signals} />
        </Card>
      </div>
    </div>
  );
}

function Field({ k, v, mono }: { k: string; v: unknown; mono?: boolean }) {
  const value = v == null || v === '' ? null : String(v);
  return (
    <div className="flex items-center justify-between gap-3 border-b py-1.5 text-sm last:border-0">
      <span className="text-muted-foreground shrink-0 whitespace-nowrap">{k}</span>
      <div className="flex min-w-0 items-center gap-1.5">
        <span
          className={cn('truncate', mono ? 'font-mono text-xs' : 'text-right')}
          title={value ?? undefined}
        >
          {value ?? '-'}
        </span>
        {value && <CopyButton text={value} />}
      </div>
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  const toast = useToast();
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // 非安全上下文 / 旧 WebView 没有 navigator.clipboard,退回 execCommand,
        // 否则 writeText 直接抛错会中断,连反馈都没有。
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        ta.remove();
      }
      setCopied(true);
      toast.success('已复制', text.length > 64 ? `${text.slice(0, 64)}…` : text);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('复制失败', '请手动选择文本复制');
    }
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      className={cn(
        'shrink-0 transition-colors',
        copied ? 'text-emerald-500' : 'text-muted-foreground/50 hover:text-foreground',
      )}
      title={copied ? '已复制' : '复制'}
    >
      {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
    </button>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <div className="text-muted-foreground text-xs">{label}</div>
      <div className="mt-1 text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}
